<template>
  <div class="summary-section" :data-aos="isInternalView ? '' : 'fade-up'" data-aos-once="true">
    <v-container>
      <v-expansion-panels v-model="panel" dark accordion multiple>
        <v-expansion-panel expand>
          <v-expansion-panel-header>
            <v-app-bar dark color="#212121">
              <v-toolbar-title style="text-align:center;">TikTok Post Analysis Summary</v-toolbar-title>
              <v-spacer></v-spacer>
              
              <v-menu
                :close-on-content-click="false"
                bottom
                left
                v-if="showRequiredActions()"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    @click="openColSettingsModal"
                  >
                    <i class="fas fa-cog fa-lg" data-toggle="tooltip" data-placement="top" title="Settings"></i>
                  </v-btn>
                </template>
              </v-menu>
              <v-btn
                v-if="showRequiredActions()"
                icon
                color="primary"
                @click="downloadSummary"
              >
                <i class="fas fa-download fa-lg" data-toggle="tooltip" data-placement="top" title="Download"></i>
              </v-btn>
              <v-btn
                v-if="isInternalView && showRequiredActions()"
                icon
                color="primary"
                @click.stop="$emit('open-analysis')"
              >
                <i class="fas fa-magic fa-lg" data-toggle="tooltip" data-placement="top" title="AI Analysis"></i>
              </v-btn>
            </v-app-bar>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card id="tiktok-analysis-summary" class="mx-auto" outlined color="#000000">
              <v-card-text>
                <div>
                  <!-- Group Filter Buttons (right aligned, no "All" button) -->
                  <div v-if="groups.length > 0" class="group-filter-btns-right">
                    <div class="group-btns">
                      <v-btn
                        v-for="group in groups"
                        :key="group.groupID"
                        :color="selectedGroup === group.groupID ? 'orange' : '#fff'"
                        :class="['group-btn', { 'active-btn': selectedGroup === group.groupID }]"
                        @click="selectedGroup = selectedGroup === group.groupID ? null : group.groupID"
                        depressed
                        small
                        style="margin-right:8px; color: #212121;"
                      >
                        {{ group.groupName || group.groupID }}
                      </v-btn>
                    </div>
                  </div>
                  <div class="text-right mb-2">
                    <!-- Save and Cancel buttons for drag-and-drop reordering -->
                    <v-btn
                      @click.native.stop
                      @click="cancelPostOrder"
                      color="warning"
                      dark
                      v-if="editPostOrder"
                      class="mr-2"
                      small
                    >
                      Cancel
                    </v-btn>

                    <v-btn
                      @click.native.stop
                      @click="savePostOrder"
                      color="success"
                      dark
                      v-if="editPostOrder"
                      class="mr-2"
                      small
                    >
                      <v-icon small>mdi-content-save</v-icon> Save Changes
                    </v-btn>
                  </div>
                  <v-data-table
                    dark
                    :headers="computedHeaders"
                    :items="filteredItems"
                    :items-per-page="15"
                    :page.sync="page"
                    item-key="key"
                    class="elevation-15 report-summary-table"
                    :key="dataTableKey"
                    v-sortable-data-table
                    @sorted="handleDataTableDragDrop"
                    :custom-sort="customSort"
                  >
                    <template v-for="(h, index) in computedHeaders" v-slot:[`header.${h.value}`]="{ header }">
                      <legend-icon :key="index" :header="h" />
                    </template>
                    <template v-slot:item.name="{ item }">
                      <div class="d-flex align-center">
                        <v-icon
                          small
                          class="move-cursor"
                        >
                          mdi-arrow-all
                        </v-icon>
                        <v-btn
                          v-if="isInternalView"
                          class="btn-reload"
                          text
                          x-small
                          color="warning"
                          @click="refreshReportInf(item.id)"
                        >
                          <v-icon dark small>
                            mdi-reload
                          </v-icon>
                        </v-btn>
                        <a class="caption" style="color:#fff" :href="profileUrl(item)" target="_blank" rel="noopener noreferrer">@{{item.name}}</a>
                      </div>
                    </template>
                    <template v-slot:[`item.url`]="{ item }">
                      <td class="text-left">
                        <v-btn
                          x-small
                          text
                          color="secondary"
                          elevation="0"
                          :href="item.url"
                          :target="'_blank'"
                        >
                          View
                        </v-btn>
                      </td>
                    </template>
                    <template v-slot:item.outreach="{ item }">
                      {{item.outreach === 0 || !item.outreach ? '-' : item.outreach | thousandSeprator}}
                    </template>
                    <template v-slot:item.views="{ item }">
                      {{item.views === 0 ? '-' : item.views | thousandSeprator}}
                    </template>
                    <template v-slot:item.likes="{ item }">
                      {{item.likes === 0 ? '-' : item.likes | thousandSeprator}}
                    </template>
                    <template v-slot:item.comments="{ item }">
                      {{item.comments === 0 ? '-' : item.comments | thousandSeprator}}
                    </template>
                    <template v-slot:item.saved="{ item }">
                      {{item.saved === 0 ? '-' : item.saved | thousandSeprator}}
                    </template>
                    <template v-slot:item.shares="{ item }">
                      {{item.shares === 0 ? '-' : item.shares | thousandSeprator}}
                    </template>
                    <template v-slot:item.reach="{ item }">
                      {{item.reach === 0 ? '-' : item.reach}}
                    </template>
                    <template v-slot:item.impressions="{ item }">
                      {{item.impressions === 0 ? '-' : item.impressions}}
                    </template>
                    <template v-slot:item.plays="{ item }">
                      {{item.plays === 0 ? '-' : showTikTokPlayTime(item.plays)}}
                    </template>
                    <template v-slot:item.watches="{ item }">
                      {{item.watches === 0 ? '-' : item.watches}}
                    </template>
                    <template v-slot:item.new_engagement_rate="{ item }">
                      {{item.new_engagement_rate === null ? '-' : item.new_engagement_rate + '%'}}
                    </template>
                    <template v-slot:item.new_engagement_rate_view="{ item }">
                      {{item.new_engagement_rate_view === null ? '-' : item.new_engagement_rate_view + '%'}}
                    </template>
                    <template v-slot:item.interaction_rate_plays="{ item }">
                      {{item.interaction_rate_plays === null ? '-' : item.interaction_rate_plays + '%'}}
                    </template>
                    <template v-slot:item.eng_rate_total_viewers="{ item }">
                      {{item.eng_rate_total_viewers === null ? '-' : item.eng_rate_total_viewers + '%'}}
                    </template>
                    <template v-slot:item.engagement_rate_sd="{ item }">
                      {{item.engagement_rate_sd === null ? '-' : item.engagement_rate_sd + '%'}}
                    </template>
                    <template v-slot:item.median_engagement_rate="{ item }">
                      {{item.median_engagement_rate === null ? '-' : item.median_engagement_rate + '%'}}
                    </template>
                    <template v-slot:item.watchedFullVideo="{ item }">
                      {{item.watchedFullVideo === null ? '-' : item.watchedFullVideo + '%'}}
                    </template>
                    <template v-slot:item.photo_views="{ item }">
                      {{item.photo_views === 0 ? '-' : item.photo_views | thousandSeprator}}
                    </template>
                    <template slot="body.append">
                      <tr style="font-weight:500;">
                        <td><strong>TOTAL</strong></td>
                        <template v-for="header in computedHeaders">
                          <td :key="header.value" v-if="header.value !== 'name'">
                            <strong>
                              <template v-if="header.value === 'live_date'">-</template>
                              <template v-else-if="header.value === 'url'">-</template>
                              <template v-else-if="header.value === 'outreach'">{{ totalOutreach === 0 ? '-' : totalOutreach | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'plays'">{{ showTikTokPlayTime(totalPlayTime) }}</template>
                              <template v-else-if="header.value === 'watches'">-</template>
                              <template v-else-if="header.value === 'likes'">{{ totalLikes === 0 ? '-' : totalLikes | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'comments'">{{ totalComments === 0 ? '-' : totalComments | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'shares'">{{ totalShares === 0 ? '-' : totalShares | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'saved'">{{ totalBookmarks === 0 ? '-' : totalBookmarks | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'interactions'">{{ totalInteractions === 0 ? '-' : totalInteractions | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'views'">{{ totalViews === 0 ? '-' : totalViews | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'reach'">{{ totalReach === 0 ? '-' : totalReach | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'impressions'">{{ totalImpressions === 0 ? '-' : totalImpressions | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'avg_views'">-</template>
                              <template v-else-if="header.value === 'avg_likes'">-</template>
                              <template v-else-if="header.value === 'avg_comments'">-</template>
                              <template v-else-if="header.value === 'avg_saves'">-</template>
                              <template v-else-if="header.value === 'avg_shares'">-</template>
                              <template v-else-if="header.value === 'new_engagement_rate'">{{ totalEngagementRate === 0 ? '-' : totalEngagementRate }} %</template>
                              <template v-else-if="header.value === 'new_engagement_rate_view'">{{ totalEngagementRateView === 0 ? '-' : totalEngagementRateView }} %</template>
                              <template v-else-if="header.value === 'interaction_rate_plays'">{{ totalInteractionRatePlays === 0 ? '-' : totalInteractionRatePlays }} %</template>
                              <template v-else-if="header.value === 'eng_rate_total_viewers'">{{ totalEngRateTotalViewers === 0 ? '-' : totalEngRateTotalViewers }} %</template>
                              <template v-else-if="header.value === 'engagement_rate_sd'"> - </template>
                              <template v-else-if="header.value === 'median_engagement_rate'"> - </template>
                              <template v-else-if="header.value === 'watchedFullVideo'">{{ totalWatchedFullVideo === 0 ? '-' : totalWatchedFullVideo }} %</template>
                              <template v-else-if="header.value === 'photo_views'">{{ totalPhotoViews === 0 ? '-' : totalPhotoViews | thousandSeprator }}</template>
                            </strong>
                          </td>
                        </template>
                      </tr>
                    </template>
                  </v-data-table>
                </div>
                <!-- <legend-section
                  :rightSections="tikTokLegends"
                ></legend-section> -->
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>
    <table-cols-show-hide-modal
      v-model="colSettingsDialog"
      title="TikTok Post Analysis Summary"
      :items="tableColsItems"
      @updated="updateTikTokAnalysisStatus"
      :isSort="true"
    />
  </div>
</template>

<style scoped>
.group-filter-btns-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 16px;
}
.group-btns {
  display: flex;
  align-items: center;
}
.group-btn {
  min-width: 48px;
  font-weight: 500;
  background: #fff !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  color: #212121 !important;
  transition: background 0.2s, color 0.2s;
}
.active-btn {
  background: orange !important;
  color: #fff !important;
}
</style>

<script>
/* eslint-disable */
import html2canvas from 'html2canvas';
import LegendSection from './Legend'
import LegendIcon from './LegendIcon'
import campaignHelper from '../views/Campaign/campaignHelper';
import TableColsShowHideModal from '@/components/scoped/custom/TableColsShowHideModal'
import config from '@/config';
import campaignAPIs from '../views/Campaign/campaignAPIs';
import { getReportProfileUrl } from '@/utils/reportPlatform'
export default {
  name: 'rpt_tiktok_summary',
  props: ['posts', 'campaign', 'defaultCols', 'isInternalView'],
  components: {
    LegendSection,
    LegendIcon,
    TableColsShowHideModal
  },
  data () {
    return {
      page: 1,
      sortBy: 'live_date',
      sortDesc: true,
      dataTableKey: 0,
      disableSaveArrange: true,
      lineUpItems: [],
      panel: [0, 1],
      tikTokLegends: [],
      colSettingsDialog: false,
      selectedGroup: null,
      tableColsItems: [],
      editPostOrder: false,
      originalPosts: [],
      headers: [
        // ...existing headers...
        // (do not omit any header, keep all as in original code)
        // ...existing code...
        {
          text: 'Influencer',
          align: 'start',
          sortable: false,
          value: 'name'
        },
        {
          text: 'Live Date',
          value: 'live_date',
          legendText: 'Live Date'
        },
        {
          text: 'Post URL',
          value: 'url',
          legendText: 'Post URL'
        },
        {
          text: 'Audience Size',
          value: 'outreach',
          legendText: 'The number of followers the creator has at the time where the post was made'
        },
        {
          text: 'Total Play Time',
          value: 'plays',
          legendText: 'The all-time data for how many total hours, minutes, and seconds this piece of content was viewed.'
        },
        {
          text: 'Average Watch Time',
          value: 'watches',
          legendText: 'How many seconds your video is viewed for'
        },
        {
          text: 'Likes',
          value: 'likes',
          legendText: 'The number of times users have tapped the “like” button on a post.'
        },
        {
          text: 'Comments',
          value: 'comments',
          legendText: 'The number of written responses to a post.'
        },
        {
          text: 'Shares',
          value: 'shares',
          legendText: 'The number of times users have reposted / shared a post.'
        },
        {
          text: 'Bookmarks',
          value: 'saved',
          legendText: 'The number of times users have saved a post to their “Saved” folder.'
        },
        {
          text: 'Interactions',
          value: 'interactions',
          legendText: 'The sum of likes, comments, saves, shares'
        },
        {
          text: 'Plays',
          value: 'views',
          legendText: 'The number of times users have viewed a post, including every looped view.'
        },
        {
          text: 'Accounts Reached',
          value: 'reach',
          legendText: 'The total number of unique users who viewed the content'
        },
        {
          text: 'Impressions',
          value: 'impressions',
          legendText: 'Total number of times content have been shown to users'
        },
        {
          text: 'Average Views',
          value: 'avg_views',
          legendText: 'Average views over the last 30 posts'
        },
        {
          text: 'Average Likes',
          value: 'avg_likes',
          legendText: 'Average likes over the last 30 posts'
        },
        {
          text: 'Average Comments',
          value: 'avg_comments',
          legendText: 'Average comments over the last 30 posts'
        },
        {
          text: 'Average Saves',
          value: 'avg_saves',
          legendText: 'The average sum of saves on the last 30 posts'
        },
        {
          text: 'Average Shares',
          value: 'avg_shares',
          legendText: 'The average sum of shares on the last 30 posts'
        },
        {
          text: 'Engagement Rate by Audience Size',
          value: 'new_engagement_rate',
          legendText: 'The total number of interactions (users who liked, commented, saved or shared the post) divided by audience size'
        },
        {
          text: 'Play Rate (by Audience Size)',
          value: 'new_engagement_rate_view',
          legendText: 'The total number of plays divided by the audience size'
        },
        {
          text: 'Engagement Rate by Plays',
          value: 'interaction_rate_plays',
          legendText: 'The total number of interactions (users who liked, commented, saved or shared the post) divided by Plays'
        },
        {
          text: 'Engagement Rate by Accounts Reached',
          value: 'eng_rate_total_viewers',
          legendText: 'The total number of interactions (users who liked, commented, saved or shared the post) divided by accounts reached'
        },
        {
          text: 'Average Engagement Rate',
          value: 'engagement_rate_sd',
          legendText: 'The average sum of engagement rate on the last 30 posts'
        },
        {
          text: 'Median Engagement Rate',
          value: 'median_engagement_rate',
          legendText: 'The middle engagement rate amongst the sorted engagement rate of all the creator’ within same tier comparison.'
        },
        {
          text: 'Watched Full Video',
          value: 'watchedFullVideo',
          legendText: 'Watched Full Video'
        },
        {
          text: 'Photo Views',
          value: 'photo_views',
          legendText: 'Photo Views'
        }
      ],
      showHideFields: [
        { name: 'liveDate', label: 'Live Date' },
        { name: 'url', label: 'Post URL' },
        { name: 'outreach', label: 'Audience Size' },
        { name: 'showPlays', label: 'Total Play Time' },
        { name: 'showWatches', label: 'Average Watch Time' },
        { name: 'likes', label: 'Likes' },
        { name: 'comments', label: 'Comments' },
        { name: 'shares', label: 'Shares' },
        { name: 'showBookmarks', label: 'Bookmarks' },
        { name: 'showInteractions', label: 'Interactions' },
        { name: 'showViews', label: 'Plays' },
        { name: 'showReach', label: 'Accounts Reached' },
        { name: 'showImpressions', label: 'Impressions' },
        { name: 'avgViews', label: 'Average Views' },
        { name: 'avgLikes', label: 'Average Likes' },
        { name: 'avgComments', label: 'Average Comments' },
        { name: 'avgSaves', label: 'Average Saves' },
        { name: 'avgShares', label: 'Average Shares' },
        { name: 'showEngagement', label: 'Engagement Rate by Audience Size' },
        { name: 'showEngagementView', label: 'Play Rate (by Audience Size)' },
        { name: 'showInteractionPlays', label: 'Engagement Rate by Plays' },
        { name: 'showEngRateTotalViewers', label: 'Engagement Rate by Accounts Reached' },
        { name: 'showAvgEngagement', label: 'Average Engagement Rate' },
        { name: 'showMediaEngagement', label: 'Median Engagement Rate' },
        { name: 'showWatchedFullVideo', label: 'Watched Full Video' },
        { name: 'showPhotoViews', label: 'Photo Views' }
      ],
      rightSection: [
        // ...existing rightSection...
        // (do not omit any section, keep all as in original code)
        // ...existing code...
        {
          title: 'Likes',
          description: 'Number of likes'
        },
        {
          title: 'Comments',
          description: 'Number of comments'
        },
        {
          title: 'Bookmarks',
          description: 'Number of bookmarks'
        },
        {
          title: 'Shares',
          description: 'Number of shares'
        },
        {
          title: 'Total Views',
          description: 'Number of plays'
        },
        {
          title: 'Reach',
          description: 'Number of unique user that seen your content'
        },
        {
          title: 'Impressions',
          description: 'Total number of times content have been shown to users'
        },
        {
          title: 'Total Play Time',
          description: 'The all-time data for how many total hours, minutes, and seconds this piece of content was viewed.'
        },
        {
          title: 'Average Watch Time',
          description: 'How many seconds your video is viewed for.'
        },
        {
          title: 'Average Views',
          description: 'Average views over the last 30 posts'
        },
        {
          title: 'Average Likes',
          description: 'Average likes over the last 30 posts'
        },
        {
          title: 'Average Comments',
          description: 'Average comments over the last 30 posts'
        },
        {
          title: 'Live Date',
          description: 'Live Date'
        },
        {
          title: 'Post URL',
          description: 'Post URL'
        },
        {
          title: 'Engagement Rate by Interactions',
          description: 'Followers divided by total interactions for this campaign'
        },
        {
          title: 'Average Engagement Rate',
          description: 'Average engagement for all advertorials and organic content'
        },
        {
          title: 'Median Engagement Rate',
          description: 'Distrubution by engagement rate for influencers having followers in the similar range/tier'
        }
      ],
      items: [],
      posts: this.posts
    }
  },
  computed: {
    sortedPosts() {
      return this.posts
    },
    currentUserRole: function() {
      return Number(this.$store.state.user.role);
    },
    groups() {
      const groupMap = new Map();
      this.sortedPosts.forEach(post => {
        if (post.groupID && post.groupName) {
          groupMap.set(post.groupID, post.groupName);
        }
        if (Array.isArray(post.reports)) {
          post.reports.forEach(report => {
            if (report.groupID && report.groupName) {
              groupMap.set(report.groupID, report.groupName);
            }
          });
        }
      });
      return Array.from(groupMap, ([groupID, groupName]) => ({
        groupID,
        groupName
      }));
    },
    filteredItems() {
      if (!this.selectedGroup) return this.sortedPosts;
      return this.sortedPosts.filter(item => {
        if (item.groupID === this.selectedGroup) return true;
        if (Array.isArray(item.reports)) {
          return item.reports.some(report => report.groupID === this.selectedGroup);
        }
        return false;
      });
    },
    computedHeaders() {
      const status = this.campaign.tikTokAnalysisStatus || {};
      const finalHeaders = [];

      // 1. Add the "Influencer" column first (always fixed)
      const nameHeader = this.headers.find(h => h.value === 'name');
      if (nameHeader) {
        finalHeaders.push(nameHeader);
      }

      // 2. Iterate through the keys of tikTokAnalysisStatus to determine order and visibility
      for (const statusKey in status) {
        if (Object.prototype.hasOwnProperty.call(status, statusKey)) {
          const isVisible = status[statusKey];
          if (isVisible) {
            // Find the corresponding header from `headers` using `showHideFields` as a map
            const field = this.showHideFields.find(f => f.name === statusKey);
            if (field) {
              const header = this.headers.find(h => h.text === field.label);
              if (header) {
                finalHeaders.push(header);
              }
            } else {
              // Fallback for keys that might directly match header values
              const header = this.headers.find(h => h.value === statusKey);
              if (header) {
                finalHeaders.push(header);
              }
            }
          }
        }
      }
      return finalHeaders;
    },
    totalOutreach() {
      let outreach = 0;
      this.filteredItems.map(item => {
        outreach += item.outreach ? Number(item.outreach) : 0
      })
      return outreach
    },
    totalViews() {
      let views = 0;
      this.filteredItems.map(item => {
        views += item.views ? Number(item.views) : 0
      })
      return views.toFixed(2)
    },
    totalBookmarks() {
      let bookmarks = 0;
      this.filteredItems.map(item => {
        bookmarks += item.saved ? Number(item.saved) : 0
      })
      return bookmarks
    },
    totalInteractions() {
      let interactions = 0;
      this.filteredItems.map(item => {
        interactions += item.interactions ? Number(item.interactions) : 0
      })
      return interactions
    },
    totalLikes() {
      let likes = 0;
      this.filteredItems.map(item => {
        likes += item.likes ? Number(item.likes) : 0
      })
      return likes
    },
    totalComments() {
      let comments = 0;
      this.filteredItems.map(item => {
        comments += item.comments ? Number(item.comments) : 0
      })
      return comments
    },
    totalShares() {
      let shares = 0;
      this.filteredItems.map(item => {
        shares += item.shares ? Number(item.shares) : 0
      })
      return shares
    },
    totalReach() {
      let reach = 0;
      this.filteredItems.map(item => {
        reach += item.reach ? Number(item.reach) : 0
      })
      return reach
    },
    totalImpressions() {
      let impressions = 0;
      this.filteredItems.map(item => {
        impressions += item.impressions ? Number(item.impressions) : 0
      })
      return impressions
    },
    totalPlays() {
      let plays = 0;
      this.filteredItems.map(item => {
        plays += item.plays ? Number(item.plays) : 0
      })
      return plays.toFixed(2)
    },
    totalWatches() {
      let watches = 0;
      this.filteredItems.map(item => {
        watches += item.watches ? Number(item.watches) : 0
      })
      return watches.toFixed(2)
    },
    totalEngagementRate() {
      let totalEng = 0;
      this.filteredItems.map(item => {
        totalEng += item.new_engagement_rate ? parseFloat(item.new_engagement_rate) : 0
      })
      return this.filteredItems.length ? ((totalEng / this.filteredItems.length)).toFixed(2) : 0
    },
    totalInteractionRatePlays() {
      let totalEng = 0;
      this.filteredItems.map(item => {
        totalEng += item.interaction_rate_plays ? parseFloat(item.interaction_rate_plays) : 0
      })
      return this.filteredItems.length ? ((totalEng / this.filteredItems.length)).toFixed(2) : 0
    },
    totalEngRateTotalViewers() {
      let totalEng = 0;
      this.filteredItems.map(item => {
        totalEng += item.eng_rate_total_viewers ? parseFloat(item.eng_rate_total_viewers) : 0
      })
      return this.filteredItems.length ? ((totalEng / this.filteredItems.length)).toFixed(2) : 0
    },
    totalEngagementRateView() {
      let totalEng = 0;
      this.filteredItems.map(item => {
        totalEng += item.new_engagement_rate_view ? parseFloat(item.new_engagement_rate_view) : 0
      })
      return this.filteredItems.length ? ((totalEng / this.filteredItems.length)).toFixed(2) : 0
    },
    totalAvgViews() {
      let avgViews = 0;
      this.filteredItems.map(item => {
        avgViews += item.avg_views ? item.avg_views : 0
      })
      return avgViews
    },
    totalAvgLikes() {
      let avgLikes = 0;
      this.filteredItems.map(item => {
        avgLikes += item.avg_likes ? item.avg_likes : 0
      })
      return avgLikes
    },
    totalAvgComments() {
      let avgComments = 0;
      this.filteredItems.map(item => {
        avgComments += item.avg_comments ? item.avg_comments : 0
      })
      return avgComments
    },
    totalPlayTime() {
      return campaignHelper.calculateTotalPlayTime(this.filteredItems)
    },
    totalWatchedFullVideo() {
      let totalData = 0;
      this.filteredItems.map(item => {
        totalData += item.watchedFullVideo ? parseFloat(item.watchedFullVideo) : 0
      })
      return this.filteredItems.length ? ((totalData / this.filteredItems.length)).toFixed(2) : 0
    },
    totalPhotoViews() {
      let totalData = 0;
      this.filteredItems.map(item => {
        totalData += item.photo_views ? Number(item.photo_views) : 0
      })
      return totalData
    }
  },
  mounted() {
    this.updateLegend();
  },
  methods: {
    profileUrl(item) {
      return getReportProfileUrl(item, 'tiktok')
    },
    customSort: function(items, index, isDesc) {
      items.sort((a, b) => {
        if (!(isNaN(a[index[0]]))) {
          if (!isDesc[0]) {
            return (a[index[0]] - b[index[0]]);
          } else {
            return (b[index[0]] - a[index[0]]);
          }
        } else {
          if (!isDesc[0]) {
            return (a[index[0]] < b[index[0]]) ? -1 : 1;
          } else {
            return (b[index[0]] < a[index[0]]) ? -1 : 1;
          }
        }
      })
      this.lineUpItems = Object.assign([], items)
      this.disableSaveArrange = false
      return items;
    },
    handleDataTableDragDrop(event, items) {
      // check and save drag n drop update for table items/ grouped items
      if (items.length === 0 || !this.showRequiredActions()) {
        return;
      }
      
      if (!this.editPostOrder) {
        this.originalPosts = JSON.parse(JSON.stringify(this.posts));
      }
      
      const itemsPerPage = 15;
      const oldIndex = ((this.page - 1) * itemsPerPage) + event.oldIndex;
      const newIndex = ((this.page - 1) * itemsPerPage) + event.newIndex;
      let tmpPosts = []
      let groupID = items[0].groupID
      if (groupID) {
        let group = this.sortedPosts.find(p => p.id === groupID)
        if (group) {
          tmpPosts = group.reports
        }
      } else {
        tmpPosts = Object.assign([], this.sortedPosts)
      }
      const movedItem = tmpPosts.splice(oldIndex, 1)[0]
      tmpPosts.splice(newIndex, 0, movedItem)
      if (groupID) {
        this.posts = this.sortedPosts.map(p => {
          if (p.id === groupID) {
            p.reports = tmpPosts.map(tmp => tmp)
          }
          return p
        })
      } else {
        this.posts = tmpPosts.map(tmp => tmp)
      }
      this.lineUpItems = []
      this.lineUpItems = Object.assign([], tmpPosts)
      this.disableSaveArrange = false
      this.editPostOrder = true
    },
    savePostOrder() {
      this.updateLineUp();
      this.editPostOrder = false;
      this.originalPosts = [];
    },
    cancelPostOrder() {
      this.posts = JSON.parse(JSON.stringify(this.originalPosts));
      this.lineUpItems = [];
      this.disableSaveArrange = true;
      this.editPostOrder = false;
      this.originalPosts = [];
    },
    async updateLineUp() {
      await this.lineUpItems.map((item, index) => {
        let data = {}
        if (item.reportGroup) {
          data.groupID = item.id
          data.lineUpIndex = index
          data.posts = item.reports.map((element, rindex) => {
            return Object.assign({}, { id: element.id, lineUpIndex: rindex })
          })
        } else {
          data = {
            reportID: item.id,
            campaignID: item.campaignID,
            lineUpIndex: index
          }
        }
        campaignAPIs.updateLineUpIndex(data)
      })
    },
    updateLegend() {
      let filteredLegend = this.rightSection.filter(element => {
        let s = this.campaign.tikTokAnalysisStatus
        if (!s.showEngagement && element.title === 'Engagement Rate by Interactions') return false
        if (!s.showAvgEngagement && element.title === 'Average Engagement Rate') return false
        if (!s.showMediaEngagement && element.title === 'Median Engagement Rate') return false
        if (!s.showViews && element.title === 'Views') return false
        if (!s.likes && element.title === 'Likes') return false
        if (!s.comments && element.title === 'Comments') return false
        if (!s.showBookmarks && element.title === 'Bookmarks') return false
        if (!s.shares && element.title === 'Shares') return false
        if (!s.showReach && element.title === 'Reach') return false
        if (!s.showImpressions && element.title === 'Impressions') return false
        if (!s.showPlays && element.title === 'Total Play Time') return false
        if (!s.showWatches && element.title === 'Average Watch Time') return false
        if (!s.avgViews && element.title === 'Average Views') return false
        if (!s.avgLikes && element.title === 'Average Likes') return false
        if (!s.avgComments && element.title === 'Average Comments') return false
        if (!s.liveDate && element.title === 'Live Date') return false
        if (!s.url && element.title === 'Post URL') return false
        return true
      });
      this.tikTokLegends = filteredLegend
    },
    async updateTikTokAnalysisStatus(items) {
      await this.updateColumnsStatus(items, 'tikTokAnalysisStatus', 'colSettingsDialog');
    },
    showRequiredActions: function() {
      if (!this.$store.state.token || this.$store.state.token === 'null' || !this.$store.state.user) {
        return false
      } else {
        return config.userRoles.CLIENT !== this.currentUserRole;
      }
    },
    showTikTokPlayTime(time) {
      return campaignHelper.showTikTokPlayTime(time)
    },
    openColSettingsModal() {
      this.initializeTikTokAnalysisTableCols();
      this.colSettingsDialog = true
    },
    initializeTikTokAnalysisTableCols() {
        this.tableColsItems = campaignHelper.getOrderedColumns(
          this.showHideFields,
          this.campaign.tikTokAnalysisStatus,
          this.defaultCols
        );
    },
    async updateColumnsStatus(items, campaignDataProp, dialogProp) {
        let newStatusObject = {};
        items.forEach((item) => {
            newStatusObject[item.key] = item.value;
        });
        let data = [
            Object.assign({}, {
                id: this.campaign.id,
                [campaignDataProp]: newStatusObject
            })
        ];
        try {
            await this.$store.dispatch('campaign/updateCampaignByIds', data);
            this.campaign[campaignDataProp] = newStatusObject;
            this[dialogProp] = false;
        } catch (error) {
            console.error('Error updating campaign settings:', error);
        }
    },
    downloadSummary() {
      const element = document.getElementById('tiktok-analysis-summary');
      html2canvas(element).then(canvas => {
        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png');
        link.download = 'tiktok-analysis-summary.png';
        link.click();
      });
    },
    refreshReportInf(id) {
      this.$emit('refreshReportInf', id)
    }
  }
}
</script>



// WEBPACK FOOTER //
// TiktokSummary.vue?58621c62